import type { Table } from "@tanstack/react-table";
import { SortableHeader } from "./SortableHeader";

function alignClass(align: "start" | "center" | "end" | undefined): string {
  if (align === "start") return "text-start";
  if (align === "end") return "text-end";
  if (align === "center") return "text-center";
  return "text-start";
}

type DataTableSkeletonProps<T> = {
  table: Table<T>;
  rowCount?: number;
  headerRowClassName?: string;
};

export function DataTableSkeleton<T>({
  table,
  rowCount = 10,
  headerRowClassName = "text-xs font-semibold uppercase tracking-wide bg-slate-100 text-slate-500",
}: DataTableSkeletonProps<T>) {
  const columnCount = table.getVisibleLeafColumns().length;

  return (
    <table
      className="w-full table-fixed border-collapse"
      aria-busy="true"
      aria-label="Loading"
    >
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id} className={headerRowClassName}>
            {headerGroup.headers.map((header) => {
              const meta = header.column.columnDef.meta;
              const align = alignClass(meta?.align);
              return (
                <th
                  key={header.id}
                  style={{ width: header.getSize() }}
                  className={`px-4 py-3 ${align} ${meta?.headerClassName ?? ""}`}
                >
                  {header.isPlaceholder ? null : (
                    <SortableHeader header={header} />
                  )}
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
      <tbody>
        {Array.from({ length: rowCount }, (_, rowIndex) => (
          <tr
            key={rowIndex}
            className="border-t border-slate-200"
            aria-hidden="true"
          >
            {Array.from({ length: columnCount }, (_, cellIndex) => (
              <td key={cellIndex} className="px-4 py-3">
                <div className="h-4 w-full max-w-48 animate-pulse rounded bg-slate-100" />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
